import { useEffect, useState } from 'react'
import { client } from '../lib/api'
import type { ProfileCompletionSnapshot } from '../../shared/onboarding'
import {
  GENDER_INTEREST_OPTIONS,
  OUTREACH_LABEL,
  OUTREACH_OPTIONS,
  parseInterestedIn,
  showOutreachFromLegacy,
  type ConnectionIntent,
  type GenderInterest,
  type OutreachPreference,
} from '../lib/ui'
import { AccountStanding } from './AccountStanding'
import { SelfieVerification } from './SelfieVerification'
import { PhotoRequestSettingsPanel } from './PhotoRequestSettings'
import { AffiliateDashboard } from './AffiliateDashboard'
import { isNativeApp } from '../lib/nativeAuth'
import { Support } from './Support'
import type { DynamicRole } from '../../shared/types'

export type MeUser = {
  id: string
  email: string
  display_name: string
  dynamic_role?: DynamicRole
  outreach_preference?: OutreachPreference
  connection_intent?: ConnectionIntent
  heading?: string | null
  bio?: string | null
  city?: string | null
  birth_year?: number | null
  accent_color?: string | null
  show_dynamics?: string
  show_outreach?: string
  min_age?: number
  max_age?: number
  max_distance_miles?: number
  require_verified?: number
  is_incognito?: number
  visibility?: 'members' | 'matches' | 'hidden'
  notify_matches?: number
  notify_messages?: number
  notify_likes?: number
  verification_status?: string
  diamond_label?: string | null
  is_admin?: number
  gender?: string | null
  interested_in?: string | null
}

type Section =
  | 'hub'
  | 'discovery'
  | 'photo-requests'
  | 'privacy'
  | 'standing'
  | 'notifications'
  | 'safety'
  | 'support'
  | 'account'
  | 'affiliate'

function parseShowOutreach(user: MeUser): OutreachPreference[] {
  if (user.show_outreach) {
    try {
      return JSON.parse(user.show_outreach) as OutreachPreference[]
    } catch {
      /* fall through */
    }
  }
  if (user.show_dynamics) {
    try {
      return showOutreachFromLegacy(JSON.parse(user.show_dynamics) as DynamicRole[])
    } catch {
      /* fall through */
    }
  }
  return []
}

function toggleGenderInterest(current: GenderInterest[], value: GenderInterest): GenderInterest[] {
  if (value === 'everyone') return ['everyone']
  const withoutEveryone = current.filter((v) => v !== 'everyone')
  if (withoutEveryone.includes(value)) {
    const next = withoutEveryone.filter((v) => v !== value)
    return next.length ? next : ['everyone']
  }
  return [...withoutEveryone, value]
}

export function Settings({
  user,
  authMethod = 'password',
  onSaved,
  onLogout,
  onNavigate,
  notify,
}: {
  user: MeUser
  authMethod?: 'password' | 'google'
  onSaved: () => void
  onLogout: () => void
  onNavigate: (view: 'wallet' | 'search' | 'admin') => void
  notify: (msg: string) => void
}) {
  const [section, setSection] = useState<Section>('hub')
  const [wants, setWants] = useState<OutreachPreference[]>(() => parseShowOutreach(user))
  const [minAge, setMinAge] = useState(user.min_age ?? 18)
  const [maxAge, setMaxAge] = useState(user.max_age ?? 99)
  const [distance, setDistance] = useState(user.max_distance_miles ?? 100)
  const [requireVerified, setRequireVerified] = useState(!!user.require_verified)
  const [isIncognito, setIsIncognito] = useState(!!user.is_incognito)
  const [visibility, setVisibility] = useState<'members' | 'matches' | 'hidden'>(user.visibility ?? 'members')
  const [notifyMatches, setNotifyMatches] = useState(user.notify_matches !== 0)
  const [notifyMessages, setNotifyMessages] = useState(user.notify_messages !== 0)
  const [notifyLikes, setNotifyLikes] = useState(user.notify_likes !== 0)
  const [interestedIn, setInterestedIn] = useState<GenderInterest[]>(() => parseInterestedIn(user.interested_in))
  const [blocked, setBlocked] = useState<Array<{ id: string; display_name: string | null }> | null>(null)
  const [saving, setSaving] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [changingPassword, setChangingPassword] = useState(false)
  const [query, setQuery] = useState('')
  const [completion, setCompletion] = useState<ProfileCompletionSnapshot | null>(null)

  useEffect(() => {
    client
      .onboardingStatus()
      .then((r) => setCompletion(r.onboarding))
      .catch(() => undefined)
  }, [user.id])

  useEffect(() => {
    setInterestedIn(parseInterestedIn(user.interested_in))
  }, [user.interested_in])

  useEffect(() => {
    if (section !== 'safety') return
    void client
      .blockedUsers()
      .then((r) => setBlocked(r.blocked))
      .catch(() => setBlocked([]))
  }, [section])

  const saveProfile = async (extra: Record<string, unknown> = {}) => {
    setSaving(true)
    try {
      await client.updateProfile({
        showOutreach: wants.length ? wants : undefined,
        minAge,
        maxAge,
        maxDistanceMiles: distance,
        requireVerified,
        interestedIn,
        ...extra,
      })
      notify('Saved')
      onSaved()
      setSection('hub')
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const deleteAccount = async () => {
    try {
      await client.deleteAccount(deletePassword)
      onLogout()
    } catch (e) {
      notify((e as Error).message)
    }
  }

  if (section === 'standing') {
    return <AccountStanding notify={notify} onBack={() => setSection('hub')} />
  }

  if (section === 'affiliate') {
    return <AffiliateDashboard notify={notify} onBack={() => setSection('hub')} />
  }

  if (section === 'photo-requests') {
    return (
      <div className="panel-page">
        <button className="text-button back-nav" onClick={() => setSection('hub')}>
          ← Back
        </button>
        <span className="eyebrow">Settings</span>
        <h1>Custom photo requests</h1>
        <PhotoRequestSettingsPanel notify={notify} />
      </div>
    )
  }

  if (section === 'discovery') {
    return (
      <div className="panel-page">
        <button className="text-button back-nav" onClick={() => setSection('hub')}>
          ← Back
        </button>
        <span className="eyebrow">Settings</span>
        <h1>Discovery preferences</h1>
        <div className="settings-form">
          <div className="field">
            <label>Interested in</label>
            <div className="chip-row">
              {GENDER_INTEREST_OPTIONS.map((opt) => {
                const active = interestedIn.includes(opt.value)
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`chip ${active ? 'selected' : ''}`}
                    onClick={() => setInterestedIn((current) => toggleGenderInterest(current, opt.value))}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="field">
            <label>Show me outreach styles</label>
            <div className="chip-row">
              {OUTREACH_OPTIONS.map((opt) => {
                const active = wants.includes(opt.value)
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`chip ${active ? 'selected' : ''}`}
                    onClick={() =>
                      setWants((w) => (active ? w.filter((d) => d !== opt.value) : [...w, opt.value]))
                    }
                  >
                    {OUTREACH_LABEL[opt.value]}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="field">
            <label>Age range</label>
            <div className="range-row">
              <input className="input" type="number" value={minAge} onChange={(e) => setMinAge(Number(e.target.value))} />
              <span>to</span>
              <input className="input" type="number" value={maxAge} onChange={(e) => setMaxAge(Number(e.target.value))} />
            </div>
          </div>
          <div className="field">
            <label>Max distance (miles)</label>
            <input className="input" type="number" value={distance} onChange={(e) => setDistance(Number(e.target.value))} />
          </div>
          <button className="button primary" onClick={() => saveProfile()} disabled={saving}>
            {saving ? 'Saving…' : 'Save preferences'}
          </button>
        </div>
      </div>
    )
  }

  if (section === 'privacy') {
    return (
      <div className="panel-page">
        <button className="text-button back-nav" onClick={() => setSection('hub')}>
          ← Back
        </button>
        <span className="eyebrow">Settings</span>
        <h1>Privacy & verification</h1>
        <SelfieVerification status={user.verification_status} notify={notify} onVerified={onSaved} />
        <div className="settings-form" style={{ marginTop: 24 }}>
          <label className="checkbox-row">
            <input type="checkbox" checked={requireVerified} onChange={(e) => setRequireVerified(e.target.checked)} />
            Only show me verified members
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={isIncognito} onChange={(e) => setIsIncognito(e.target.checked)} />
            Incognito browsing (don&apos;t record profile views)
          </label>
          <div className="field">
            <label>Profile visibility</label>
            <select className="input" value={visibility} onChange={(e) => setVisibility(e.target.value as typeof visibility)}>
              <option value="members">Visible to all members</option>
              <option value="matches">Matches only</option>
              <option value="hidden">Hidden from discovery</option>
            </select>
          </div>
          <button
            className="button primary"
            onClick={() =>
              saveProfile({
                isIncognito,
                visibility,
              })
            }
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save privacy settings'}
          </button>
        </div>
      </div>
    )
  }

  if (section === 'notifications') {
    return (
      <div className="panel-page">
        <button className="text-button back-nav" onClick={() => setSection('hub')}>
          ← Back
        </button>
        <span className="eyebrow">Settings</span>
        <h1>Notifications</h1>
        <p className="page-sub">Choose what we notify you about. Push and email delivery coming soon.</p>
        <div className="settings-form">
          <label className="checkbox-row">
            <input type="checkbox" checked={notifyMatches} onChange={(e) => setNotifyMatches(e.target.checked)} />
            New matches
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={notifyMessages} onChange={(e) => setNotifyMessages(e.target.checked)} />
            New messages
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={notifyLikes} onChange={(e) => setNotifyLikes(e.target.checked)} />
            Someone liked you
          </label>
          <button
            className="button primary"
            onClick={() =>
              saveProfile({
                notifyMatches,
                notifyMessages,
                notifyLikes,
              })
            }
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save notification preferences'}
          </button>
        </div>
      </div>
    )
  }

  if (section === 'support') {
    return (
      <div className="panel-page">
        <button className="text-button back-nav" onClick={() => setSection('hub')}>
          ← Back
        </button>
        <span className="eyebrow">Settings</span>
        <h1>Help &amp; support</h1>
        <Support notify={notify} />
      </div>
    )
  }

  if (section === 'safety') {
    return (
      <div className="panel-page">
        <button className="text-button back-nav" onClick={() => setSection('hub')}>
          ← Back
        </button>
        <span className="eyebrow">Settings</span>
        <h1>Safety</h1>
        <p className="rules-lead" style={{ marginBottom: 16 }}>
          No hidden rules. No invisible penalties. Our standards are published and apply equally to everyone.
        </p>
        <p className="page-sub">
          Report members from their profile or chat. If your account is restricted, see Account standing for the
          specific rule, action taken, duration, and how to appeal.
        </p>
        <a className="button secondary" href="#/legal/community">
          Read full Safety &amp; Rules
        </a>
        <div className="settings-card" style={{ marginTop: 24 }}>
          <div style={{ padding: '16px 18px' }}>
            <small>Blocked members</small>
            {blocked === null ? (
              <div className="loading-center" style={{ padding: 16 }}>
                <div className="spinner" />
              </div>
            ) : blocked.length === 0 ? (
              <p className="page-sub" style={{ marginTop: 8 }}>
                You haven&apos;t blocked anyone.
              </p>
            ) : (
              <ul className="blocked-list">
                {blocked.map((b) => (
                  <li key={b.id}>
                    <span>{b.display_name ?? 'Member'}</span>
                    <button
                      type="button"
                      className="button secondary"
                      onClick={() => {
                        void client
                          .unblockUser(b.id)
                          .then(() => {
                            notify('Unblocked')
                            setBlocked((list) => list?.filter((x) => x.id !== b.id) ?? [])
                          })
                          .catch((e) => notify((e as Error).message))
                      }}
                    >
                      Unblock
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (section === 'account') {
    return (
      <div className="panel-page">
        <button className="text-button back-nav" onClick={() => setSection('hub')}>
          ← Back
        </button>
        <span className="eyebrow">Settings</span>
        <h1>Account management</h1>
        <p className="page-sub">{user.email}</p>
        <div className="settings-form" style={{ marginBottom: 16 }}>
          {authMethod === 'google' ? (
            <>
              <p className="page-sub">You sign in with Google. To set a password, use email reset.</p>
              <button
                type="button"
                className="button secondary"
                onClick={() => {
                  void client.requestPasswordReset(user.email).then((r) => notify(r.message))
                }}
              >
                Email me a password link
              </button>
            </>
          ) : (
            <>
              <div className="field">
                <label>Current password</label>
                <input
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              <div className="field">
                <label>New password</label>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  minLength={10}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="button primary"
                disabled={changingPassword || newPassword.length < 10 || !currentPassword}
                onClick={() => {
                  setChangingPassword(true)
                  void client
                    .changePassword(currentPassword, newPassword)
                    .then((r) => {
                      notify(r.message ?? 'Password updated')
                      setCurrentPassword('')
                      setNewPassword('')
                    })
                    .catch((e) => notify((e as Error).message))
                    .finally(() => setChangingPassword(false))
                }}
              >
                {changingPassword ? 'Updating…' : 'Change password'}
              </button>
            </>
          )}
        </div>
        <div className="settings-card">
          <button onClick={onLogout}>
            <span>
              <small>Session</small>
              <strong>Sign out</strong>
            </span>
            <i>›</i>
          </button>
          <button onClick={() => setShowDelete(true)}>
            <span>
              <small>Danger zone</small>
              <strong style={{ color: 'var(--danger)' }}>Delete account</strong>
            </span>
            <i>›</i>
          </button>
        </div>
        {showDelete && (
          <div className="modal-backdrop" onClick={() => setShowDelete(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog">
              <h2>Delete your account?</h2>
              <p>Enter your password to confirm. This hides your profile and signs you out everywhere.</p>
              <div className="field">
                <input
                  className="input"
                  type="password"
                  placeholder="Password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button className="button secondary" onClick={() => setShowDelete(false)}>
                  Cancel
                </button>
                <button
                  className="button primary"
                  style={{ background: 'var(--danger)', color: '#fff' }}
                  onClick={deleteAccount}
                  disabled={!deletePassword}
                >
                  Delete my account
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="panel-page">
      <span className="eyebrow">Settings</span>
      <h1>Settings</h1>
      <p className="page-sub">Account, privacy, invites, and billing.</p>

      {completion && !completion.complete && (
        <div className="profile-completion-banner" role="status">
          <div className="profile-completion-head">
            <strong>Profile {completion.percent}% complete</strong>
            <span>Finish your profile tab to stay visible in discovery</span>
          </div>
          <div className="profile-completion-bar">
            <i style={{ width: `${completion.percent}%` }} />
          </div>
        </div>
      )}

      <div className="field settings-search">
        <input
          className="input"
          type="search"
          placeholder="Search settings…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search settings"
        />
      </div>
      <div className="settings-card">
        {(
          [
            {
              small: 'Affiliate dashboard',
              strong: 'Invite link · earn credits',
              keywords: 'affiliate invite referral link share credits earn grow',
              go: () => setSection('affiliate'),
            },
            {
              small: 'Custom photo requests',
              strong: 'Accept paid requests on your terms',
              keywords: 'photo request custom paid creator boundaries categories selfie outfit monetize',
              go: () => setSection('photo-requests'),
            },
            {
              small: 'Discovery preferences',
              strong: 'Age, distance, outreach filters',
              keywords: 'discovery preferences age range distance miles filters outreach styles matching',
              go: () => setSection('discovery'),
            },
            {
              small: 'Privacy & verification',
              strong: user.verification_status === 'verified' ? 'Verified' : 'Manage verification',
              keywords: 'privacy verification selfie verify camera badge verified only face match photo',
              go: () => setSection('privacy'),
            },
            {
              small: 'Account standing',
              strong: 'Status, notices, appeals',
              keywords: 'account standing status notices enforcement appeals moderation rules restricted banned',
              go: () => setSection('standing'),
            },
            {
              small: 'Upgrade & credits',
              strong: 'Go Plus · Buy credits',
              keywords: 'upgrade billing plus subscription credits buy purchase payment ad-free ads stripe wallet monetize',
              go: () => onNavigate('wallet'),
            },
            {
              small: 'Notifications',
              strong: 'Alerts and email',
              keywords: 'notifications alerts email push',
              go: () => setSection('notifications'),
            },
            {
              small: 'Safety',
              strong: 'Reports and guidelines',
              keywords: 'safety reports guidelines rules standards block report community',
              go: () => setSection('safety'),
            },
            {
              small: 'Help & support',
              strong: 'AI assistant · Feedback · Bug reports',
              keywords: 'help support assistant ai chat contact feedback bug report issue problem question team human',
              go: () => setSection('support'),
            },
            {
              small: 'Account management',
              strong: 'Sign out, delete account',
              keywords: 'account management sign out logout delete remove password session',
              go: () => setSection('account'),
            },
            ...(user.is_admin
              ? [
                  {
                    small: 'Admin',
                    strong: 'Platform overview',
                    keywords: 'admin platform overview moderation verifications support tickets',
                    go: () => onNavigate('admin'),
                  },
                ]
              : []),
            {
              small: 'Search',
              strong: 'Browse all compatible profiles',
              keywords: 'search browse profiles compatible people find',
              go: () => onNavigate('search'),
            },
          ] as { small: string; strong: string; keywords: string; go: () => void }[]
        )
          .filter((entry) => {
            const q = query.trim().toLowerCase()
            if (!q) return true
            return (
              entry.small.toLowerCase().includes(q) ||
              entry.strong.toLowerCase().includes(q) ||
              entry.keywords.includes(q)
            )
          })
          .map((entry) => (
            <button key={entry.small} type="button" onClick={entry.go}>
              <span>
                <small>{entry.small}</small>
                <strong>{entry.strong}</strong>
              </span>
              <i>›</i>
            </button>
          ))}
      </div>
      {query.trim() && (
        <p className="page-sub" style={{ marginTop: 8 }}>
          Can't find it? Ask the assistant in Help &amp; support.
        </p>
      )}

      <p className="profile-hub-attr">© PairVelle · Operated by PairVelle · Built by Manticore Technologies</p>

      {!isNativeApp() && (
        <button className="text-button back-nav" onClick={() => (window.location.hash = '/')}>
          Return to public site
        </button>
      )}
    </div>
  )
}
